import './LocationListTableComponent.scss'
import React, {useCallback} from 'react';
import {ITableColumn} from '../../../shared/models/table.model';
import {ILocation} from '../../../shared/models/location.model'
import SwitchComponent from "../../../shared/components/core/switch/switchComponent";
import {CommonService} from "../../../shared/services";
import {APIConfig, ImageConfig} from "../../../constants";
import {IAPIResponseType} from "../../../shared/models/api.model";
import ChipComponent from "../../../shared/components/chip/ChipComponent";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import {Link} from "react-router-dom";
import TableWrapperV2Component
    from "../../../shared/components/table-components/table-wrapper-v2/TableWrapperV2Component";
import {IFacility} from "../../../shared/models/facility.model";
import TooltipComponent from "../../../shared/components/tooltip/tooltipComponent";
import {useDispatch, useSelector} from "react-redux";
import {IRootReducerState} from "../../../store/reducers";
import commonService from "../../../shared/services/common.service";
import {getLocationListLite} from "../../../store/actions/staticData.action";
import {ITimelineItem} from "../../../shared/models/timeline.model";

const MODULE_NAME = "LocationList"

interface LocationListTableComponentProps {
    filterData?: {};
    isColumnFilterVisible: boolean
}

const LOCATION_DETAILS = 'location_details';
const PRIMARY_CONTACT = 'primary_contact';
const TEST_AND_PANELS = 'test_and_panels';
const PROVIDER_OR_PHYSICIAN = 'Provider_or_Physician';
const CRITICAL_DETAILS = "critical_details";
const BILLING_DETAILS = "billing_details";
const ACCOUNT_PREFERENCES = "account_preferences";
const BLOOD_DRAW_INFORMATION = "blood_draw_information";
const ADMIN_DETAILS = "admin_details";

const STEPS: ITimelineItem[] = [
    {
        id: LOCATION_DETAILS,
        title: "Location Details",
    },
    {
        id: ADMIN_DETAILS,
        title: "Admin Details",
    },
    {
        id: PRIMARY_CONTACT,
        title: "Primary Contact",
    },
    {
        id: PROVIDER_OR_PHYSICIAN,
        title: "Provider/Physician",
    },
    {
        id: TEST_AND_PANELS,
        title: "Test and Panels",
    },
    {
        id: CRITICAL_DETAILS,
        title: "Critical Details",
    },
    {
        id: BILLING_DETAILS,
        title: "Billing Details",
    },
    {
        id: ACCOUNT_PREFERENCES,
        title: "Account Preferences",
    },
    {
        id: BLOOD_DRAW_INFORMATION,
        title: "Blood Draw Information",
    },

    // {
    //     id: EMAIL_VERIFICATION,
    //     title: "Email Verification",
    // }
];
const LocationListTableComponent = (props: LocationListTableComponentProps) => {

    const {filterData, isColumnFilterVisible} = props;

    const {currentUser} = useSelector((state: IRootReducerState) => state.account);

    const dispatch = useDispatch();

    const columns: ITableColumn[] = [
        {
            id: 'name',
            label: 'Location Name',
            isDefaultColumn: true,
            width: "250",
            fixed: "left",
            content: (item: IFacility) => (
                <>
                    {
                        (item?.name).length > 20 ?
                            <TooltipComponent
                                tooltip={CommonService.capitalizeFirstLetterOfEachWord(item?.name)}
                                placement={"top"}
                                arrow={true}
                            >
                                <>
                                    <div className={"ellipses-for-table-data"}>
                                        {CommonService.capitalizeFirstLetterOfEachWord(item?.name)}
                                    </div>
                                </>
                            </TooltipComponent> :
                            <>
                                {CommonService.capitalizeFirstLetterOfEachWord(item?.name)}
                            </>
                    }
                </>
            )
        },
        {
            id: 'type', label: 'Location Type', isDefaultColumn: true, width: '210',
            content: (item: IFacility) => (
                <>
                    {
                        item?.type && item?.type?.length > 23 ?
                            <TooltipComponent
                                tooltip={CommonService.capitalizeFirstLetterOfEachWord(item?.type)}
                                placement={"top"}
                                arrow={true}
                            >
                                <div className={"ellipses-for-table-data"}>
                                    {CommonService.capitalizeFirstLetterOfEachWord(item?.type)}
                                </div>
                            </TooltipComponent> :
                            <>
                                {CommonService.capitalizeFirstLetterOfEachWord(item?.type || '-')}
                            </>
                    }
                </>
            )
        },
        {
            id: 'facilityName', label: 'Facility Name', isDefaultColumn: true,
            content: (item: ILocation) => (
                <>
                    {item?.facilityDetails ? <>{CommonService.capitalizeFirstLetterOfEachWord(item?.facilityDetails?.name)}</> : "NA"}
                </>
            )
        },
        {
            id: 'onBoardedBy', label: 'Added by', isDefaultColumn: false, align: "center",
            content: (item: ILocation) => (
                <>
                    {item?.createdByDetails ? <>{CommonService.capitalizeFirstLetterOfEachWord(item?.createdByDetails?.firstName)} {CommonService.capitalizeFirstLetterOfEachWord(item?.createdByDetails?.lastName)}</> : "NA"}
                </>
            )
        },
        {
            id: 'createdAt', label: 'Added Date & Time', isDefaultColumn: false, align: "center", width: 180,
            content: (item: ILocation) => (
                item.createdAt ?
                    <>
                        {commonService.formatDate(item?.createdAt)}
                        &nbsp;|&nbsp;
                        {commonService.formatTime(item?.createdAt)}
                    </> : <>NA</>
            )
        },
        {
            id: 'city', label: 'City', isDefaultColumn: false, align: "center", width: "150",
            content: (item: ILocation) => (
                <>
                    {item?.addressDetails?.city ? <>{item?.addressDetails?.city}</> : <>NA</>}
                </>
            )
        },
        {
            id: 'state', label: 'State', isDefaultColumn: false, align: "center", width: "150",
            content: (item: ILocation) => (
                <>
                    {item?.addressDetails?.state ? <>{item?.addressDetails?.state}</> : <>NA</>}
                </>
            )
        },
        {
            id: 'status', label: 'Location Status', isDefaultColumn: true, width: "150",
            align: 'center',
            content: (item: ILocation) => (
                item.status ?
                    <>
                        {item?.status ? <>
                            <ChipComponent className={`min-width-60  ${item?.status}`}
                                           label={CommonService.capitalizeFirstLetter(item?.status)}
                                           variant={'outlined'}/>
                        </> : <>-</>
                        }
                    </> : <>NA</>
            )
        },
        {
            id: 'isActive',
            label: commonService.checkAccess('Location',
                "locationToggle", currentUser?.userAccessModules) ?
                'Active/Inactive' : "",
            width: commonService.checkAccess('Location',
                "locationToggle", currentUser?.userAccessModules) ?
                "150" : "10",
            align: "center",
            isDefaultColumn: true,
            content: (item: ILocation, index: any) => (
                <React.Fragment>
                    {
                        commonService.checkAccess('Location',
                            "locationToggle", currentUser?.userAccessModules) ?
                            <SwitchComponent
                                size={"small"}
                                checked={item.isActive}
                                disabled={(item?.status === 'draft')}
                                onUpdate={() => handleLocationToggle(item, index)}/> :
                            <></>
                    }
                </React.Fragment>
            )
        },
        {
            id: 'actions',
            label: '',
            isDefaultColumn: true,
            align: "center",
            hideInColumnPreferences:true,
            width: "100",
            fixed: "right",
            content: (item: ILocation, index: any) => (
                <React.Fragment>
                    <div className="table-actions" id={`view_location_` + index}>
                        {item?.status === "draft" ? <>
                                <Link
                                    to={CommonService._routeConfig.LocationAddRoute() + "?lastCompletedStep=" + (item?.lastCompletedStep ? item?.lastCompletedStep + 1 : 0) + `&locationId=${item?.id}&step=${STEPS[item?.lastCompletedStep ? (item?.lastCompletedStep + 1) : 1]?.id}`}>
                                    <div className="table-action">
                                        <ChevronRightIcon/>
                                    </div>
                                </Link>
                            </> :
                            <Link to={CommonService._routeConfig.LocationDetailsTestOrderRoute(item?.id)}>
                                <div className="table-action">
                                    <ChevronRightIcon/>
                                </div>
                            </Link>
                        }
                    </div>
                </React.Fragment>
            )
        },
    ];

    const onToggleLocation = useCallback((item: any, index: any) => {
        CommonService._location.toggleActiveStatus(item.id, {})
            .then((response: IAPIResponseType<any>) => {
                CommonService._communications.TableWrapperRefreshSubject.next({
                    moduleName: MODULE_NAME
                });
                dispatch(getLocationListLite());
                CommonService._alert.showToast(response?.message || `Location has been  ${item?.isActive ? "deactivated" : "activated"}`, "success");
            })
            .catch((error: any) => {
                CommonService._alert.showToast(error?.error || `Error while ${item?.isActive ? "deactivating" : "activating"} the location`, "error");
            });
    }, [dispatch]);


    const handleLocationToggle = useCallback((item: any, index: any) => {
        CommonService.openConfirmationDialog({
            confirmationTitle: 'Please confirm!',
            confirmationSubTitle: `Are you sure you want to ${item?.isActive ? "deactivate" : "activate"} this location?`,
            disableEscKeyClose: false,
            disableBackdropClose: true,
            imagePath: `${ImageConfig.ConfirmationImage}`,
            direction: "up",
            yes: {
                text: `${item?.isActive ? "Deactivate" : "Activate"}`,
                color: 'primary'
            },
            no: {
                text: "Cancel",
                color: 'primary'
            }
        })
            .then(() => {
                onToggleLocation(item, index);
            })
            .catch(() => {
            })
    }, [onToggleLocation]);


    return (

        <div className="table-list-container">
            <TableWrapperV2Component
                isPaginated={true}
                columns={columns}
                url={APIConfig.LOCATION_LIST.URL}
                method={APIConfig.LOCATION_LIST.METHOD}
                extraPayload={filterData}
                isColumnFilterVisible={isColumnFilterVisible}
                moduleName={MODULE_NAME}
            />
        </div>
    )
};

export default LocationListTableComponent;
